import React from 'react';
import { Column } from 'components';
import { useProduct } from '../hooks/useProduct';
import { useProductLivePreview } from '../hooks/useProductLivePreview';
import { useProductPrice } from '../hooks/useProductPrice';
import { useMedia } from '../hooks/useMedia';
import { PDPForm } from './Form';
import { getGatsbySiteUrl } from 'helpers/env';
import { Wrapper } from './styled';
import { Rating } from 'types/Product';
import PhotoSliderV2 from 'components/PhotoSliderV2';
import { useStickyBox } from 'react-sticky-box';
import ProductHeader from './ProductHeader';
import { PdpAccordion } from './PdpAccordion';
import { useProductUseCase } from '../hooks/useProductUseCase';
import { generateLivePreviewImage } from '../helpers';
import ProductHeaderMobile from './ProductHeaderMobile';
import { DeviceSize, useWindowSize } from '../../../hooks/useWindowSize';
import ProductHeaderNew from './ProductHeaderNew';
import { useShowNewPdpStructure } from '../../../hooks/splits/useNewPdpStructureEnabled';

type PDPProps = {
  rating: Rating;
};

// TODO: Once the split is at 100% we will handle this in a more data-driven way
const productsToRetainTopDetails = ['curation-services'];
export const pdpTopException = (sku: string): boolean => productsToRetainTopDetails.includes(sku);

export const PDP = ({ rating }: PDPProps): JSX.Element => {
  const { product } = useProduct();
  const { photos } = useMedia();
  const price = useProductPrice();
  const livePreviewUrl = useProductLivePreview();
  const useCaseValues = useProductUseCase();
  const { deviceSize } = useWindowSize();

  const isMobile = deviceSize === DeviceSize.SMALL;

  const showMobileHeader = isMobile && product.sku !== 'email-giftcard';

  const showNewPdpStructure = useShowNewPdpStructure(product.sku);

  // This is the photos list without the live preview image.  We are using this to prevent
  // re-rendering of the image carousel when live preview images change.
  const mobilePhotos = [...photos];
  if (livePreviewUrl) {
    const livePreviewImage = generateLivePreviewImage(livePreviewUrl);
    photos.unshift(livePreviewImage);
  }
  const stickyBoxRef = useStickyBox();

  const hasNoRating = rating === undefined;
  const hasSmallRatingCountAndLowScore = rating?.totalReviews < 5 && rating?.score < 4;
  const shouldHideRating = hasNoRating || hasSmallRatingCountAndLowScore;

  // For new structure, image/option columns each take 6 of 12 columns for a 50/50 split
  // Old structure is 8/4 or 75/25 split
  const imageColumnWidth = showNewPdpStructure ? 6 : 8;
  const optionsColumnWidth = showNewPdpStructure ? 6 : 4;

  return (
    <div className="PdpApp">
      <Wrapper>
        <Column size={12} medium={imageColumnWidth} className="z-pt-tiny photo-slider-column">
          <PhotoSliderV2 photos={photos} mobilePhotos={mobilePhotos} livePreviewUrl={livePreviewUrl}
                         badges={product?.badges} />
        </Column>
        <Column size={12} medium={optionsColumnWidth}>
          <div ref={stickyBoxRef}>
            {/* If the test wins, we can just show this unconditionally. Otherwise we will revert back to */}
            {/* displaying ProductHeader/ProductHeaderMobile, but those components could both be simplified */}
            {/* into a single component most likely */}
            {showNewPdpStructure ?
              <ProductHeaderNew
                productTitle={useCaseValues.title}
                price={price}
                product={product}
                siteUrl={getGatsbySiteUrl() || ''}
                rating={!shouldHideRating && rating}
              />
              :
              (showMobileHeader ? (
                <ProductHeaderMobile
                  productTitle={useCaseValues.title}
                  price={price}
                  product={product}
                  siteUrl={getGatsbySiteUrl() || ''}
                  rating={!shouldHideRating && rating}
                />
              ) : (
                <ProductHeader
                  productTitle={useCaseValues.title}
                  price={price}
                  product={product}
                  siteUrl={getGatsbySiteUrl() || ''}
                  rating={!shouldHideRating && rating}
                />
              ))}
            <PDPForm displayNewPdpTop />
            <PdpAccordion />
          </div>
        </Column>
      </Wrapper>
    </div>
  );
};
